/*
 * BF2 product page (PDP) interactions.
 *
 * Smooth open/close for the native <details> accordions
 * (.bf2-pdp-acc). We keep the native element and its semantics: the summary
 * click is intercepted only to animate the details height, then the real
 * open state is committed on finish. Falls back to instant toggle when the
 * Web Animations API is missing or the user prefers reduced motion, so the
 * accordions still work with no JS surprises.
 */
import { createDebugger } from "~/scripts/utils/debug.js";

const debug = createDebugger("bf2-pdp", false);

const DURATION = 300;
const EASING = "cubic-bezier(0.25, 0.1, 0.25, 1)";

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function enhanceAccordion(details) {
  const summary = details.querySelector(".bf2-pdp-acc__head");
  const body = details.querySelector(".bf2-pdp-acc__body");
  if (!summary || !body) return;

  let animation = null;
  let state = "idle"; // idle | expanding | shrinking

  const canAnimate = () =>
    typeof details.animate === "function" && !prefersReducedMotion();

  const settle = (open) => {
    details.open = open;
    details.style.height = "";
    details.removeAttribute("data-bf2-animating");
    animation = null;
    state = "idle";
  };

  const run = (from, to, open) => {
    if (animation) animation.cancel();
    details.setAttribute("data-bf2-animating", "");
    animation = details.animate(
      { height: [`${from}px`, `${to}px`] },
      { duration: DURATION, easing: EASING }
    );
    animation.addEventListener("finish", () => settle(open), { once: true });
    animation.addEventListener("cancel", () => {}, { once: true });
  };

  const expand = () => {
    state = "expanding";
    const from = details.offsetHeight;
    // Reveal body so we can measure the full open height.
    details.open = true;
    const to = summary.offsetHeight + body.offsetHeight;
    run(from, to, true);
  };

  const shrink = () => {
    state = "shrinking";
    const from = details.offsetHeight;
    const to = summary.offsetHeight;
    run(from, to, false);
  };

  summary.addEventListener("click", (event) => {
    if (!canAnimate()) {
      // Let the browser toggle natively.
      return;
    }
    event.preventDefault();
    // Freeze current height so the transition has a start value.
    details.style.height = `${details.offsetHeight}px`;

    if (state === "shrinking" || !details.open) {
      expand();
    } else if (state === "expanding" || details.open) {
      shrink();
    }
  });

  debug.log("accordion enhanced", details);
}

// Fabric PDP: the "Visualisera tyget på en möbel" CTA opens the textile studio
// seeded with this fabric. Keep the seeded variant in sync with the variant the
// customer has picked, so the studio opens on the exact colour they were viewing.
function syncFabricCta() {
  const cta = document.querySelector("[data-studio-fabric-cta]");
  if (!cta) return;
  const base = cta.getAttribute("data-studio-fabric-base");
  const section = cta.closest('[data-section-type="product"]');
  if (!base || !section) return;
  section.addEventListener("variantChange", (event) => {
    const variant = event.detail && event.detail.variant;
    if (!variant) return;
    cta.setAttribute("href", base + "&fabric=" + variant.id);
    debug.log("fabric cta variant synced", variant.id);
  });
}

// Wallpaper PDP: the "Se tapeten i ditt hem" CTA deep-links to the room
// visualizer. Keep the variant in the link in sync with the colorway the
// customer has picked, so the visualizer opens on that exact colorway.
function syncWallpaperCta() {
  const cta = document.querySelector("[data-bf-visualize][data-visualize-base]");
  if (!cta) return;
  const base = cta.getAttribute("data-visualize-base");
  const section = cta.closest('[data-section-type="product"]');
  if (!base || !section) return;
  section.addEventListener("variantChange", (event) => {
    const variant = event.detail && event.detail.variant;
    if (!variant) return;
    cta.setAttribute("href", base + "&variant=" + variant.id);
    debug.log("wallpaper cta variant synced", variant.id);
  });
}

// Sample CTA ("Beställ tygprov" / "Beställ tapetprov"): keep the hidden sample
// variant input in sync with the colorway the customer has selected on the
// parent product, using the parent-variant-title -> sample-variant-id map
// rendered server-side by bf2-sample-cta.liquid. No exact match (e.g. the
// sample product doesn't carry that colorway) falls back to the sample
// product's first available variant.
function syncSampleCta() {
  const wrap = document.querySelector("[data-sample-cta]");
  if (!wrap) return;
  const form = wrap.querySelector("[data-sample-form]");
  const input = wrap.querySelector("[data-sample-variant-input]");
  const mapEl = wrap.querySelector("[data-sample-variant-map]");
  const submit = wrap.querySelector("[data-sample-submit]");
  const section = wrap.closest('[data-section-type="product"]');
  if (!form || !input || !mapEl || !submit || !section) return;

  const fallbackId = submit.getAttribute("data-sample-fallback-id");
  let variantMap = {};
  try {
    variantMap = JSON.parse(mapEl.textContent);
  } catch (err) {
    debug.log("sample cta: could not parse variant map", err);
  }

  const applyVariantTitle = (title) => {
    const matchId = title && variantMap[title];
    input.value = matchId || fallbackId || "";
  };

  section.addEventListener("variantChange", (event) => {
    const variant = event.detail && event.detail.variant;
    if (!variant) return;
    applyVariantTitle(variant.title);
    debug.log("sample cta variant synced", input.value);
  });

  // AJAX add-to-cart: POST /cart/add.js, then dispatch the same
  // "ajaxProduct:added" bubbling CustomEvent the theme's own add-to-cart
  // flow dispatches on its form (see main.bundle.js CartForm.addItem).
  // theme.CartDrawer listens for that on document and calls
  // buildCart() + open(), so the existing drawer opens and refreshes
  // itself, no need to duplicate that logic here.
  submit.addEventListener("click", (event) => {
    event.preventDefault();
    if (!input.value) return;

    const errorEl = wrap.querySelector("[data-sample-error]");
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = "";
    }
    submit.disabled = true;

    // Payloaden byggs explicit i stallet for FormData(form). Tidigare var
    // det ett riktigt formular, och da fick allt en app rakade injicera i
    // det folja med i anropet.
    const formData = new FormData();
    formData.append("id", input.value);
    formData.append("quantity", "1");

    const action = form.getAttribute("data-sample-action") || "/cart/add";
    fetch(action + ".js", {
      method: "POST",
      body: formData,
      headers: { Accept: "application/json" },
    })
      .then((response) =>
        response.json().then((data) => ({ ok: response.ok, data }))
      )
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.description || "Kunde inte lägga till provet");
        form.dispatchEvent(
          new CustomEvent("ajaxProduct:added", {
            detail: { product: data, addToCartBtn: submit },
            bubbles: true,
          })
        );
        debug.log("sample added to cart", data);
      })
      .catch((err) => {
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = errorEl.getAttribute("data-sample-error-text") || "";
        }
        debug.log("sample cta add failed", err);
      })
      .finally(() => {
        submit.disabled = false;
      });
  });
}

// The swatch group prints the selected value next to the option name
// ("UTFÖRANDE · Delft | Blå"). It is rendered from option.selected_value in
// Liquid, so without this it keeps showing whatever was selected on page load
// no matter which swatch the customer clicks.
function syncOptionLabels() {
  document.querySelectorAll('[data-section-type="product"]').forEach((section) => {
    const labels = Array.from(section.querySelectorAll("[data-bf2-option-value]"));
    if (!labels.length) return;
    section.addEventListener("variantChange", (event) => {
      const variant = event.detail && event.detail.variant;
      if (!variant || !variant.options) return;
      labels.forEach((el) => {
        const value = variant.options[Number(el.getAttribute("data-bf2-option-value"))];
        el.textContent = value ? "\u00b7 " + value : "";
      });
      debug.log("option labels synced", variant.options);
    });
  });
}

// Kampanjpris pa PDP:n.
//
// Rabatten ar en automatisk rabatt i kassan och satter darfor aldrig
// compare_at_price. Liquid renderar ratt pris vid sidladdning, men temats egen
// variant-JS skriver om [data-product-price] till fullpris vid varje
// variantbyte. Den har modulen skriver tillbaka kampanjpriset efterat.
//
// Procentsatsen kommer fran data-bf2-kampanj, som sektionen bara satter nar
// snippets/bf-kampanj.liquid slappt igenom produkten. Ligger rabattens omfang
// eller datum fel ar det darfor snippeten som ska andras, inte den har filen.
function syncKampanjPris() {
  document.querySelectorAll('[data-section-type="product"][data-bf2-kampanj]').forEach((section) => {
    const procent = Number(section.getAttribute("data-bf2-kampanj"));
    if (!procent) return;

    const priceEl = section.querySelector("[data-product-price]");
    const compareEl = section.querySelector("[data-compare-price]");
    const compareWrap = section.querySelector("[data-product-price-wrap]");
    if (!priceEl) return;

    const money = (cents) => {
      const currency = window.theme && window.theme.Currency;
      const format = window.theme && window.theme.moneyFormat;
      if (currency && typeof currency.formatMoney === "function") {
        return currency.formatMoney(cents, format);
      }
      return String(Math.round(cents / 100));
    };

    const apply = (variant) => {
      if (!variant || variant.price == null) return;
      const kampanjPris = Math.round((variant.price * (100 - procent)) / 100);
      priceEl.innerHTML = money(kampanjPris);
      priceEl.classList.add("bf2-pdp__price-current--sale");
      if (compareEl) compareEl.innerHTML = money(variant.price);
      if (compareWrap) compareWrap.classList.remove("hide");
      debug.log("kampanjpris", { procent, fran: variant.price, till: kampanjPris });
    };

    // Temats variant-JS skriver priset synkront i sin egen lyssnare. rAF lagger
    // var omskrivning efter den, oavsett vilken ordning lyssnarna registrerades.
    section.addEventListener("variantChange", (event) => {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;
      window.requestAnimationFrame(() => apply(variant));
    });
  });
}

// Rabattstegen 10 %/15 % for tyger och tapeter.
//
// Pengarna dras av en automatisk rabatt i kassan nar tyg+tapet-raderna i
// kundvagnen tillsammans nar troskeln. Den har modulen speglar det pa
// produktsidan sa kunden ser summan och rabatten innan kassan: pris x antal
// for den har produkten plus tyg+tapet-raderna som redan ligger i kundvagnen
// (/cart.js). Rabatten visas pa den har produktens del, avrundad nerat till
// 10 ore per rad som Shopify gor.
//
// Stegen kommer fran data-bf2-tiers pa snippets/bf2-textile-discount.liquid,
// som sektionen bara renderar for ratt produkter (typ + primary_listing, ingen
// kampanj). Andras rabatten i admin ar det snippeten som ska andras.
//
// Valuta: trosklarna star i SEK (butiksvalutan). Nar kunden valt en annan
// valuta ar priset pa sidan och /cart.js redan i den valutan, sa trosklarna
// raknas om med Shopify.currency.rate, samma kurs som kassan anvander for
// rabattens minimibelopp. Textraderna ("Handla for … och fa 10 %") renderas
// da med tomma belopp av snippeten och fylls i har. Saknas kursen visas
// varken rader eller rabatt (hellre inget an fel belopp).
function syncTextileDiscount() {
  // product_type i /cart.js foljer sidans sprak (Tyger -> Fabrics pa /en).
  const TEXTILE_TYPES = ["tyger", "tyg", "tapeter", "tapet", "fabrics", "fabric", "wallpapers", "wallpaper"];

  document.querySelectorAll('[data-section-type="product"]').forEach((section) => {
    const box = section.querySelector("[data-bf2-textile-discount]");
    if (!box) return;

    const shopCurrency = String(box.getAttribute("data-bf2-shop-currency") || "SEK").toUpperCase();
    const cartCurrency = String(box.getAttribute("data-bf2-cart-currency") || shopCurrency).toUpperCase();
    let fxRate = 1;
    if (cartCurrency !== shopCurrency) {
      const sc = window.Shopify && window.Shopify.currency;
      const active = sc && String(sc.active || "").toUpperCase();
      fxRate = sc && active === cartCurrency ? Number(sc.rate) : NaN;
      if (!Number.isFinite(fxRate) || fxRate <= 0) {
        debug.log("textile discount: ingen valutakurs", { cartCurrency, shopCurrency });
        return;
      }
    }
    // SEK avrundas nerat till 10 ore per rad (Shopifys oresavrundning);
    // andra valutor till narmaste hundradel.
    const roundMinor = (minor) => (cartCurrency === "SEK" ? Math.floor(minor / 10) * 10 : Math.round(minor));

    // Varumarken utan rabattsteg (Dedar, Sam 2026-10-05): deras rader i
    // kundvagnen raknas inte in i troskeln, precis som den automatiska rabatten
    // i admin bara ser kollektionerna "Rabattberattigade tyger/tapeter".
    const excludedVendors = String(box.getAttribute("data-bf2-excluded-vendors") || "")
      .split(",")
      .map((v) => v.trim().toLowerCase())
      .filter(Boolean);
    const tiers = String(box.getAttribute("data-bf2-tiers") || "")
      .split(",")
      .map((pair) => pair.split(":").map(Number))
      .filter((t) => t.length === 2 && t[0] > 0 && t[1] > 0)
      .map(([kr, procent]) => ({ kr, minor: Math.round(kr * 100 * fxRate), rate: procent / 100, procent }))
      .sort((a, b) => b.minor - a.minor);
    if (!tiers.length) return;

    // Toppraden (box, under huvudpriset) och totalblocket (ovanfor mangdraden)
    // ar tva olika delar av snippeten, sa elementen soks i hela sektionen.
    const topFinalEl = box.querySelector("[data-bf2-tier-top-final]");
    const topBadgeEl = box.querySelector("[data-bf2-tier-top-badge]");
    const summaryEl = section.querySelector("[data-bf2-tier-summary]");
    const qtyEl = section.querySelector("[data-bf2-tier-qty]");
    const grossEl = section.querySelector("[data-bf2-tier-gross]");
    const discountRowEl = section.querySelector("[data-bf2-tier-discount-row]");
    const discountLabelEl = section.querySelector("[data-bf2-tier-discount-label]");
    const discountEl = section.querySelector("[data-bf2-tier-discount]");
    const netRowEl = section.querySelector("[data-bf2-tier-net-row]");
    const netEl = section.querySelector("[data-bf2-tier-net]");
    const cartEl = section.querySelector("[data-bf2-tier-cart]");
    if (
      !topFinalEl || !topBadgeEl || !summaryEl || !qtyEl || !grossEl ||
      !discountRowEl || !discountLabelEl || !discountEl || !netRowEl || !netEl || !cartEl
    ) return;

    const tQty = box.getAttribute("data-bf2-t-qty") || "";
    const tDiscount = box.getAttribute("data-bf2-t-discount") || "";
    const tCart = box.getAttribute("data-bf2-t-cart") || "";
    const tBadge = box.getAttribute("data-bf2-t-badge") || "";
    const fill = (template, values) =>
      template.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) => (key in values ? values[key] : m));

    const qtyInput = section.querySelector('.js-qty__num[name="quantity"]');
    let priceMinor = Number(box.getAttribute("data-bf2-price")) || 0;
    let cartTextileMinor = 0;

    const money = (cents) => {
      const currency = window.theme && window.theme.Currency;
      const format = window.theme && window.theme.moneyFormat;
      if (currency && typeof currency.formatMoney === "function") {
        return currency.formatMoney(cents, format);
      }
      return String(Math.round(cents / 100)) + " kr";
    };

    const quantity = () => {
      const n = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      return Number.isFinite(n) && n > 0 ? n : 1;
    };

    // Textraderna i annan valuta an SEK: fyll i de omraknade trosklarna och
    // visa listan (snippeten renderar den dold tills beloppen finns).
    const tiersList = section.querySelector("[data-bf2-tiers-list]");
    if (tiersList && tiersList.hasAttribute("data-bf2-tiers-convert")) {
      let filled = 0;
      tiersList.querySelectorAll("[data-bf2-tier-amount]").forEach((el) => {
        const kr = Number(el.getAttribute("data-bf2-tier-amount"));
        const tier = tiers.find((t) => t.kr === kr);
        if (!tier) return;
        el.textContent = money(tier.minor);
        filled += 1;
      });
      if (filled) tiersList.hidden = false;
    }

    const render = () => {
      if (!priceMinor) {
        box.hidden = true;
        summaryEl.hidden = true;
        return;
      }
      const qty = quantity();
      const lineMinor = priceMinor * qty;
      const combinedMinor = lineMinor + cartTextileMinor;
      const tier = tiers.find((t) => combinedMinor >= t.minor) || null;
      const rate = tier ? tier.rate : 0;
      const discountMinor = roundMinor(lineMinor * rate);
      const finalMinor = lineMinor - discountMinor;
      // Styckpriset efter rabatt, samma avrundning per enhet.
      const unitFinalMinor = priceMinor - roundMinor(priceMinor * rate);

      // Raden under huvudpriset: rabatterat STYCKPRIS + "−10 % rabatt"
      // (Sam 2026-09-24). Huvudpriset ovanfor ror vi inte. Bara nar rabatten
      // slar in.
      if (discountMinor > 0) {
        topFinalEl.textContent = money(unitFinalMinor);
        topBadgeEl.textContent = fill(tBadge, { percent: tier.procent });
        box.hidden = false;
      } else {
        box.hidden = true;
      }

      // Sammanfattningen ovanfor mangdraden, tre rader: Antal + totalt fore
      // rabatt, Rabatt (10 %) i rott, Totalt efter rabatt. Vid 1 st utan
      // rabatt ar summan bara huvudpriset igen, da doljs blocket.
      if (discountMinor > 0 || qty > 1) {
        qtyEl.textContent = fill(tQty, { count: qty });
        grossEl.textContent = money(lineMinor);
        summaryEl.hidden = false;
      } else {
        summaryEl.hidden = true;
      }
      if (discountMinor > 0) {
        discountLabelEl.textContent = fill(tDiscount, { percent: tier.procent });
        discountEl.textContent = "−" + money(discountMinor);
        netEl.textContent = money(finalMinor);
        discountRowEl.hidden = false;
        netRowEl.hidden = false;
      } else {
        discountRowEl.hidden = true;
        netRowEl.hidden = true;
      }

      // Nar kundvagnen bidrar till troskeln sags det, annars ser kunden en
      // rabatt som inte gar att harleda ur summan pa sidan.
      if (discountMinor > 0 && cartTextileMinor > 0) {
        cartEl.textContent = fill(tCart, { amount: money(cartTextileMinor) });
        cartEl.hidden = false;
      } else {
        cartEl.hidden = true;
      }
      debug.log("textile discount", { qty, lineMinor, cartTextileMinor, rate, discountMinor, cartCurrency, fxRate });
    };

    const fetchCart = () => {
      fetch("/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } })
        .then((r) => r.json())
        .then((cart) => {
          let sum = 0;
          (cart.items || []).forEach((item) => {
            const type = String(item.product_type || "").toLowerCase().trim();
            // original_line_price ar FORE rabatt (line_price i /cart.js ar efter
            // radens automatiska rabatt). Troskeln i admin raknas pa ordinarie
            // pris, sa det ar den summan som ska in. Hittat 2026-09-24: med
            // line_price underskattades kundvagnen sa fort den redan hade rabatt.
            const vendor = String(item.vendor || "").toLowerCase().trim();
            if (excludedVendors.indexOf(vendor) >= 0) return;
            if (TEXTILE_TYPES.indexOf(type) >= 0) sum += Number(item.original_line_price || item.line_price || 0);
          });
          cartTextileMinor = sum;
          render();
        })
        .catch((err) => debug.log("textile discount cart fetch failed", err));
    };

    section.addEventListener("variantChange", (event) => {
      const variant = event.detail && event.detail.variant;
      if (!variant || variant.price == null) return;
      priceMinor = Number(variant.price) || 0;
      render();
    });

    // Temats QtySelector skriver input.value pa +/- utan att fyra change, sa
    // klicket fangas har (bubblar efter knappens egen lyssnare) utover
    // change/input for manuell inmatning. setTimeout och inte rAF: rAF
    // pausas i bakgrundsflikar och summan skulle da sta kvar pa fel antal.
    if (qtyInput) {
      qtyInput.addEventListener("change", render);
      qtyInput.addEventListener("input", render);
    }
    section.addEventListener("click", (event) => {
      if (event.target.closest && event.target.closest(".js-qty__adjust")) {
        window.setTimeout(render, 0);
      }
    });

    // Kundvagnen kan andras i cart-drawern, i en annan flik eller genom att
    // den har produkten (eller ett prov) laggs i den.
    document.addEventListener("ajaxProduct:added", fetchCart);
    document.addEventListener("cart:build", fetchCart);
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) fetchCart();
    });

    render();
    fetchCart();
  });
}

function init() {
  const accordions = document.querySelectorAll(".bf2-pdp-acc");
  accordions.forEach(enhanceAccordion);
  syncFabricCta();
  syncWallpaperCta();
  syncSampleCta();
  syncOptionLabels();
  syncKampanjPris();
  syncTextileDiscount();
  if (accordions.length) debug.log("init", accordions.length);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
